import { useNavigate } from "react-router-dom";
import React, { useEffect, useState, ChangeEvent } from "react";
import * as trainingActions from "../../actions/trainingActions";
import { useDispatch, useSelector } from "react-redux";
import TrainingListItem from "./TrainingListItem";
import TrainingListLabelInput from "./TrainingListLabelInput";
import Training from "../../types/Training";
import TrainingSummary from "./TrainingSummary";
import { AppDispatch, RootState } from "../../store/configureStore";

export interface ListingDates {
  listSince: Date;
  listTo: Date;
}

// Keys for LocalStorage
const STORAGE_KEY_SINCE = "trainingListSince";
const STORAGE_KEY_TO = "trainingListTo";

function TrainingList() {
  const dispatch = useDispatch<AppDispatch>(); // Typed dispatch for thunk support
  const navigate = useNavigate();
  const userStore = useSelector((state: RootState) => state.loggedInUser);
  const trainingsStore = useSelector((state: RootState) => state.trainingList);

  // **Initialize Dates**
  const getInitialDates = (): ListingDates => {
    const storedSince = localStorage.getItem(STORAGE_KEY_SINCE);
    const storedTo = localStorage.getItem(STORAGE_KEY_TO);

    if (storedSince && storedTo) {
      return {
        listSince: new Date(storedSince),
        listTo: new Date(storedTo),
      };
    } else {
      // Default if no values in localStorage
      const initEndDate = new Date();
      initEndDate.setHours(23, 59, 59, 999);
      const initStartDate = new Date(initEndDate.getTime() - 1000 * 60 * 60 * 24 * 14);
      initStartDate.setHours(0, 0, 0, 0);

      // Save default values in localStorage
      localStorage.setItem(STORAGE_KEY_SINCE, initStartDate.toISOString());
      localStorage.setItem(STORAGE_KEY_TO, initEndDate.toISOString());

      return {
        listSince: initStartDate,
        listTo: initEndDate,
      };
    }
  };

  const [listingDates, setListingDates] = useState<ListingDates>(getInitialDates);
  const [showSummary, setShowSummary] = useState(false);

  // **Load Trainings when Dates or User Changes**
  useEffect(() => {
    if (!userStore?.id) return;
  
    dispatch(
      trainingActions.loadTrainingsForDateRangeAndUser(
        userStore.id,
        listingDates.listSince,
        listingDates.listTo
      )
    );
  }, [dispatch, userStore, listingDates]); // Add 'listingDates'

  // **Handle Date Change**
  const handleListingDate = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.value === "") {
      return;
    }

    const { id, value } = event.target;

    setListingDates((prevDates) => {
      const updatedDates = {
        ...prevDates,
        [id === "trainingListSince" ? "listSince" : "listTo"]: new Date(value),
      };

      // Update LocalStorage with new dates
      localStorage.setItem(STORAGE_KEY_SINCE, updatedDates.listSince.toISOString());
      localStorage.setItem(STORAGE_KEY_TO, updatedDates.listTo.toISOString());

      return updatedDates;
    });
  };

  const handleAddTraining = () => {
    navigate("/trainings/add");
  };

  const formatDateTimeLocal = (date: Date): string => {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      return "";
    }
    return date.toISOString().slice(0, 16);
  };

  const renderTrainingItems = () => {
    if (!trainingsStore || trainingsStore.length === 0) {
      return (
        <tr>
          <td colSpan={5} className="text-center">
            Nie znaleziono treningów.
          </td>
        </tr>
      );
    }

    return trainingsStore.map((listItem: Training) => (
      <TrainingListItem key={listItem.id} training={listItem} />
    ));
  };

  return (
    <div className="card my-2">
      <div className="card-header d-flex flex-wrap align-items-center gap-2">
        <div className="flex-grow-0"><b>Treningi</b></div>
        
          <div className="d-flex flex-wrap">
            <div className="mr-4">
              <TrainingListLabelInput
                label="Od:"
                id="trainingListSince"
                type="datetime-local"
                value={formatDateTimeLocal(listingDates.listSince)}
                onChange={handleListingDate}
                placeholder="Treningi od"
              />
            </div>
            <div className="mr-4">
              <TrainingListLabelInput
                label="Do:"
                id="trainingListTo"
                type="datetime-local"
                value={formatDateTimeLocal(listingDates.listTo)}
                onChange={handleListingDate}
                placeholder="Treningi do"
              />
            </div>
          </div>
          <div className="d-flex">
            <button
              type="button"
              id="showHideSummary"
              className="btn btn-info"
              onClick={() => setShowSummary((prev) => !prev)}
            >
              {showSummary ? "Ukryj Podsumowanie" : "Podsumowanie"}
            </button>
            <button
              type="button"
              id="addAddTraining"
              className="btn btn-success mx-2"
              onClick={handleAddTraining}
            >
              Dodaj
            </button>
          </div>
        
      </div>
      <div className="card-body">
        {showSummary && <TrainingSummary trainings={trainingsStore} />}
        <table className="table table-striped table-hover">
          <thead>
            <tr>
              <th>Data</th>
              <th>Nazwa</th>
              <th>Typ treningu</th>
              <th>Dystans</th>
              <th>Akcje</th>
            </tr>
          </thead>
          <tbody>{renderTrainingItems()}</tbody>
        </table>
      </div>
    </div>
  );
}

export default TrainingList;
